import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PortalLayout from './PortalLayout';
import PortalPageHeader from './PortalPageHeader';
import PortalStatusPill from './PortalStatusPill';
import { portalCardSx } from './portalStyles';
import PortalProjectService from '../../services/PortalProjectService';

const STATUS_LABELS = {
    PENDING: 'Pending',
    ONGOING: 'Ongoing',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
};

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PortalProjectsPage() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        PortalProjectService.getProjects()
            .then(data => setProjects(data || []))
            .catch(() => setError('Failed to load projects. Please try again.'))
            .finally(() => setLoading(false));
    }, []);

    return (
        <PortalLayout>
            <PortalPageHeader title="Projects" subtitle="Your active and completed projects" />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {!loading && !error && projects.length === 0 && (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 6, textAlign: 'center' }}>
                    <FolderOpenOutlinedIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                        No projects yet
                    </Typography>
                </Paper>
            )}

            {!loading && !error && projects.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={portalCardSx}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Project name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Start date</TableCell>
                                <TableCell>End date</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {projects.map((project) => (
                                <TableRow
                                    key={project.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/portal/projects/${project.id}`)}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                            {project.displayName || project.name}
                                        </Typography>
                                        {project.displayName && project.name !== project.displayName && (
                                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                {project.name}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <PortalStatusPill status={project.status} label={STATUS_LABELS[project.status]} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: '#475569' }}>
                                            {formatDate(project.startDate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: '#475569' }}>
                                            {formatDate(project.endDate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <ChevronRightIcon sx={{ color: '#cbd5e1', display: 'block' }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </PortalLayout>
    );
}
